import { useTranslation } from 'react-i18next';
import genbaMark from '@assets/projectMerge/genba-mark.png';
import { AlertIcon, RefreshIcon, SearchIcon } from '@utils/icons';
import type { IGenbaSite } from '@interfaces/IProjectMerge';
import { Button } from '@components/ui/Button/Button';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { ConnectPrompt, SkeletonList, StatePanel } from './PaneState';
import type { ILinkedInfo } from './SiteRow';
import { GenbaRow } from './SiteRow';
import pane from './pane.module.css';
import styles from './GenbaPane.module.css';

/** Pane status filter — keyed off the stable Genba status code (§6-5), plus the
    "in-operation" grouping (all but completed/canceled) and "all". */
export type GenbaStatusFilter =
  | 'in-operation'
  | 'before-construction'
  | 'under-construction'
  | 'completion'
  | 'all';

/** Pane data-availability state, derived from the query lifecycle. `noCreds`
    (never connected → Connect) and `reauth` (session expired → Reconnect) are
    distinct connect-prompt states; Genba has no refresh, so both route to the
    same `/connect/genba` form but carry different copy. */
export type PaneViewState = 'loading' | 'data' | 'empty' | 'error' | 'noCreds' | 'reauth';

interface IGenbaPaneProps {
  viewState: PaneViewState;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: GenbaStatusFilter;
  onStatusFilterChange: (value: GenbaStatusFilter) => void;
  rows: IGenbaSite[];
  /** Total matching the current filter. */
  total: number;
  selected: string | null;
  onSelect: (number: string) => void;
  /** Genba number -> link partner (other side) for already-linked rows. */
  linkedMap: Record<string, ILinkedInfo>;
  onRetry: () => void;
  onConnect: () => void;
}

const STATUS_OPTS: { key: GenbaStatusFilter; labelKey: string }[] = [
  { key: 'in-operation', labelKey: 'projectMerge.genbaPane.statusActive' },
  { key: 'before-construction', labelKey: 'projectMerge.status.beforeConstruction' },
  { key: 'under-construction', labelKey: 'projectMerge.status.underConstruction' },
  { key: 'completion', labelKey: 'projectMerge.status.completion' },
  { key: 'all', labelKey: 'projectMerge.genbaPane.statusAll' },
];

/** Left board pane — Genba Plus sites (plan reality). */
export function GenbaPane({
  viewState,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  rows,
  total,
  selected,
  onSelect,
  linkedMap,
  onRetry,
  onConnect,
}: IGenbaPaneProps) {
  const { t } = useTranslation();
  const showTools = viewState === 'data' || viewState === 'empty';

  return (
    <section className={`${pane.pane} ${styles.genba}`} aria-label={t('projectMerge.genbaPane.ariaLabel')}>
      <header className={pane.head}>
        <div className={pane.id}>
          <span className={pane.mark}>
            <img src={genbaMark} alt="" />
          </span>
          <span className={pane.idText}>
            <span className={pane.eyebrow}>{t('projectMerge.genbaPane.eyebrow')}</span>
            <span className={pane.name}>{t('projectMerge.genbaPane.name')}</span>
          </span>
          <span className={pane.count}>{t('projectMerge.genbaPane.count', { count: total })}</span>
        </div>

        {showTools ? (
          <>
            <div className={pane.tools}>
              <SearchInput
                value={search}
                onChange={onSearchChange}
                placeholder={t('projectMerge.genbaPane.searchPlaceholder')}
                ariaLabelClear={t('projectMerge.genbaPane.clearSearch')}
              />
            </div>
            {/* Bespoke (not ui/SegmentedControl): this is a flat 5-segment,
                border-joined bar with a navy-filled active segment, sitting
                flush in the pane header. SegmentedControl renders a content-
                sized pill (grey rail + sliding white thumb, 64px-min segments)
                — a visual regression for a 5-way filter with long JP labels. */}
            <div className={styles.statusFilter} role="group" aria-label={t('projectMerge.genbaPane.statusFilterLabel')}>
              {STATUS_OPTS.map((o) => {
                const on = statusFilter === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    className={on ? styles.statusOn : ''}
                    aria-pressed={on}
                    onClick={() => onStatusFilterChange(o.key)}
                  >
                    {t(o.labelKey)}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </header>

      {viewState === 'loading' ? <SkeletonList rows={5} /> : null}

      {viewState === 'noCreds' ? (
        <ConnectPrompt
          titleKey="projectMerge.paneState.noCredsTitle"
          bodyKey="projectMerge.paneState.noCredsBody"
          ctaKey="projectMerge.paneState.connect"
          onConnect={onConnect}
        />
      ) : null}

      {viewState === 'reauth' ? (
        <ConnectPrompt
          titleKey="projectMerge.paneState.reauthTitle"
          bodyKey="projectMerge.paneState.reauthBody"
          ctaKey="projectMerge.paneState.reconnect"
          onConnect={onConnect}
        />
      ) : null}

      {viewState === 'error' ? (
        <StatePanel
          variant="error"
          icon={<AlertIcon size={24} />}
          title={t('projectMerge.paneState.errorGenbaTitle')}
          actions={
            <Button variant="secondary" size="sm" onClick={onRetry}>
              <RefreshIcon size={14} />
              {t('projectMerge.paneState.retry')}
            </Button>
          }
        >
          {t('projectMerge.paneState.errorGenbaBody')}
        </StatePanel>
      ) : null}

      {viewState === 'empty' ? (
        <StatePanel variant="neutral" icon={<SearchIcon size={22} />} title={t('projectMerge.paneState.emptyGenbaTitle')}>
          {t('projectMerge.paneState.emptyGenbaBody')}
        </StatePanel>
      ) : null}

      {viewState === 'data' ? (
        <ScrollArea className={pane.list} viewportClassName={pane.listBody}>
          {rows.map((s) => (
            <GenbaRow
              key={s.number}
              site={s}
              selected={selected === s.number}
              linkedInfo={linkedMap[s.number]}
              onSelect={onSelect}
            />
          ))}
        </ScrollArea>
      ) : null}
    </section>
  );
}
